import { Form, Formik } from 'formik';
import React, { FC, useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { getMasterDetailGroupTypes, getUiTypes, storeCustomField } from '../../services/customFieldsService';
import { useDialog } from '../dialog/DialogContext';
import Wizard from '../wizard/wizard';
import { WizardSummaryContext } from '../wizard/wizardSummaryContext';
import CustomFieldForm from './customFieldForm';
import { CustomFieldsSummaryForm } from './customFieldsSummaryForm';
import MasterDetailGroupType from './masterDetailGroupTypeForm';

interface CustomFieldsWizardProps {
    onClose: () => void;
    isOpen: boolean;
    setBusy: (isBusy) => void;
    categoryId?: number;
    totalEnrolments?: number;
    setRefreshFields?: any
};

const initialState = {
    name: '',
    tooltip: '',
    uiType: '',
};

const CustomFieldsWizard: FC<CustomFieldsWizardProps> = ({ setRefreshFields, onClose, isOpen, setBusy, categoryId = -1, totalEnrolments = undefined }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState(0);
    const [uiTypeOptions, setUiTypeOptions] = useState(new Array(0));
    const [masterGroupTypeOptions, setMasterGroupTypeOptions] = useState(new Array(0));
    const [currentItem, setCurrentItem] = useState(initialState) as any;
    const [wizardTitle, setWizardTitle] = useState(translate.byKey(TranslationKeys.customField));
    const [summary, setSummary] = useState(new Array(0));
    const dialog = useDialog();

    const [sideBarSteps, setSideBarSteps] = useState([{
        title: translate.byKey(TranslationKeys.details),
        description: translate.byKey(TranslationKeys.describeThisField),
        substeps: []
    },
    {
        title: translate.byKey(TranslationKeys.group),
        description: translate.byKey(TranslationKeys.pending),
        substeps: []
    },
    {
        title: translate.byKey(TranslationKeys.summary),
        description: '',
        substeps: []
    }]);

    useEffect(() => {
        (async () => {
            getUiTypes(translate)
                .then(uitTypes => {
                    const allowedIds = ['1', '2', '32']
                    let allowedFields = [] as any
                    uitTypes.filter(uiType => {
                        if (allowedIds.includes(uiType.value)) {
                            allowedFields.push(uiType)
                        }
                    })
                    setUiTypeOptions(allowedFields)
                });

            getMasterDetailGroupTypes().then((masterGroupTypes) => {
                setMasterGroupTypeOptions(masterGroupTypes)
            })
        })();
    }, []);

    const onNext = (event: any) => {
        setStep(step + 1);
    };

    const onFieldsSubmit = (formFields) => {
        setCurrentItem(formFields);
        const newCustomField = {
            // id: categoryId,
            name: formFields.name,
            tooltip: formFields.tooltip,
            required: formFields.required ? 1 : 0,
            enabled: formFields.enabled ? 1 : 0,
            uiType: formFields.uiType,
            masterDetailGroupType: formFields.masterDetailGroupType,
            newGroupName: formFields.newGroupName
        };

        storeCustomField(newCustomField)
            .then(() => {
                toast.success(translate.byKey(TranslationKeys.fieldSaved));
                onClose()
                setRefreshFields(true)
                setBusy(false);
            })
            .catch((failure) => {
                const message = failure['messageKey'] ? translate.byKey(failure['messageKey']) : translate.byKey(TranslationKeys.applicationError);
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message });
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const onBack = () => {
        setStep(step - 1)
    };

    const onWizardClose = () => {
        setStep(0);
        setCurrentItem(initialState);
        onClose();
    }

    const setSideBarStepDescription = (index, newDescription) => {
        const newSideBarSteps = sideBarSteps.map((step, stepIndex) => {
            return { ...step, description: stepIndex === index ? newDescription : step.description };
        });
        setSideBarSteps(newSideBarSteps);
    }

    const contentPages = [
        <CustomFieldForm
            key={1}
            onNext={onNext}
            options={uiTypeOptions}
            masterGroupTypeOptions={masterGroupTypeOptions}
            setSideBarStepDescription={newDescription => setSideBarStepDescription(0, newDescription)}>
        </CustomFieldForm>,
        <MasterDetailGroupType
            key={2}
            onBack={onBack}
            onNext={onNext}
            options={uiTypeOptions}
            masterGroupTypeOptions={masterGroupTypeOptions}
            setSideBarStepDescription={newDescription => setSideBarStepDescription(1, newDescription)}>
        </MasterDetailGroupType>,
        <CustomFieldsSummaryForm
            key={3}
            onBack={onBack}>
        </CustomFieldsSummaryForm>
    ];

    const updateSummary = (accessor: string, newDisplayValue: string) => {
        if (summary) {
            const existingSummaryEntryIndex = summary.findIndex(item => item.value === accessor);
            const newSummaryEntry = { value: accessor, label: newDisplayValue };
            if (existingSummaryEntryIndex >= 0) {
                summary.splice(existingSummaryEntryIndex, 1, newSummaryEntry);
                setSummary([...summary]);
            }
            else {
                summary.push(newSummaryEntry);
                setSummary([...summary]);
            }
        }
    }

    const getContextValue = useCallback(
        () => (
            {
                summary: [...summary],
                updateSummary
            }
        ),
        [summary, setSummary],
    )

    return <WizardSummaryContext.Provider value={getContextValue()}>
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            <Formik
                enableReinitialize={true}
                initialValues={currentItem}
                onSubmit={onFieldsSubmit}>
                <Form>
                    <Wizard
                        name={wizardTitle}
                        step={step}
                        onClose={onWizardClose}
                        sidebarSteps={sideBarSteps}>
                        {contentPages}
                    </Wizard>
                </Form>
            </Formik>
        </Modal>
    </WizardSummaryContext.Provider>;
}

export default CustomFieldsWizard;


